import { computed, observable, runInAction, action } from 'mobx'
import { type StreamDownloader } from '@/features/payouts/stream-downloader'
import { task } from 'mobx-task'
import {
  type ProPayoutStore,
  type ListHistoricalTransactionsQuery,
} from './ProPayoutStore'
import { ProPayoutSummary } from './ProPayoutSummary'
import { type DateRange, DateRangeFilter } from '@/infra/DateRangeFilter'
import {
  catchError,
  combineLatest,
  defer,
  EMPTY,
  firstValueFrom,
  map,
  switchMap,
} from 'rxjs'
import { shareLatest, withAbortSignal } from '@/utils/rx-util'
import { type ProHistoricalTransaction } from './ProHistoricalTransaction'
import { ProHistoricalTransactionViewModel } from './ProHistoricalTransactionViewModel'
import { ViewModelList } from '@/infra/view-model-list'
import { InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import type { CursorPageQuery } from '@/infra/paging'
import { FiltersViewModel, FilterItemSource } from '@/infra/FiltersViewModel'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { TransactionSourceType } from '@/features/payouts/transaction-source-type'
import { SelectFilter } from '@/infra/SelectFilter'
import { noop } from 'lodash-es'

/**
 * Historical transactions query, without paging.
 */
type HistoricalTransactionsQuery = Omit<
  ListHistoricalTransactionsQuery,
  keyof CursorPageQuery
>

/**
 * Filter for historical transactions.
 */
export interface ProTransactionHistoryFilter {
  completedRange: DateRange
  transactionSource: TransactionSourceType[]
}

/**
 * View model for the Pro Payouts view.
 */
export class ProPayoutsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Transaction view model list.
   */
  private readonly _transactionVms

  /**
   * The query to use.
   */
  private readonly _query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ]),
  ).pipe(
    map(([searchText, filters]): HistoricalTransactionsQuery => ({
      searchText,
      dateCompleted: filters.completedRange,
      transactionSource: filters.transactionSource,
    })),
    shareLatest(),
  )

  /**
   * Fetch pipeline.
   */
  private readonly _fetch$ = this._query$.pipe(
    switchMap((query) =>
      withAbortSignal((signal) =>
        this.transactionsScroller.fetch({ filter: query, signal }),
      ).pipe(
        // The retry alert has reported the failure.
        catchError(() => EMPTY),
      ),
    ),
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<ProTransactionHistoryFilter>({
    definitions: {
      completedRange: {
        icon: 'date-completed',
        title: 'Date',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      transactionSource: {
        icon: 'transaction-source',
        title: 'Source',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'User',
                value: 'User',
              },
              {
                title: 'Job',
                value: 'Job',
              },
            ]),
            true,
          ),
      },
    },
  })

  /**
   * Scroller for transactions.
   */
  readonly transactionsScroller = new InfiniteScrollProvider<
    ProHistoricalTransaction,
    HistoricalTransactionsQuery
  >({
    limit: 200,
    fetchItems: ({ paging, signal, filter = {} }) => {
      const withRetry = this._createAlertRetryWrapper(
        'Unable to load transactions',
        signal,
      )

      return withRetry(() =>
        this._proPayoutStore.listHistoricalTransactions(
          { ...filter, ...paging },
          signal,
        ),
      )
    },
  })

  /**
   * The pro payout summary.
   */
  @observable.shallow accessor summary = ProPayoutSummary.default()

  constructor(
    private readonly _proPayoutStore: ProPayoutStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _downloader: StreamDownloader,
  ) {
    this._transactionVms = new ViewModelList<
      ProHistoricalTransaction,
      ProHistoricalTransactionViewModel
    >({
      create: (model) => new ProHistoricalTransactionViewModel(model),
      models: () => this.transactionsScroller.items.slice(),
    })

    this._lifecycle.manage(this._fetch$)
    this._lifecycle.manage(this.filters)
    this._lifecycle.manage(this._transactionVms)
  }

  /**
   * Gets the total count of transactions that match the server-based filter.
   */
  @computed
  get transactionsCount() {
    return this.transactionsScroller.total
  }

  /**
   * The transactions that have been loaded.
   */
  @computed
  get transactions() {
    return this._transactionVms.items
  }

  /**
   * Whether the transactions table shows, rather than the empty state.
   */
  @computed
  get showTable() {
    return (
      this.transactionsScroller.fetch.pending || this.transactions.length > 0
    )
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  @computed
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // A failed summary leaves the empty default summary showing.
    void this.fetchSummary().catch(noop)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Pages through all transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const handle = await this._downloader.requestDownload({
      suggestedName: 'transaction-history.csv',
      fileTypeDescription: 'CSV file',
      accept: { 'text/csv': ['.csv'] },
    })
    if (!handle) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Exporting', 'This may take a moment.')
      .show()
    try {
      // Get the query that is used for the current list.
      const query = await firstValueFrom(this._query$)

      // Download the historical transactions, pipe them to the file.
      await this._proPayoutStore
        .downloadHistoricalTransactionsAsCsv({
          workspaceIds: query.workspaceIds,
          dateCompleted: query.dateCompleted,
          searchText: query.searchText,
          transactionSource: query.transactionSource,
        })
        .then((csv) => handle.download(csv))

      alert
        .withState('success')
        .saying(
          'Export completed!',
          `The transaction history was exported to ${handle.fileName}.`,
        )
        .autoDismiss()
    } catch (err) {
      alert.dismiss()
      return this._createAlertErrorHandler('Exporting failed')(err)
    }
  })

  /**
   * Fetches the pro payout summary.
   */
  fetchSummary = task.resolved(async () => {
    const signal = this._lifecycle.signal
    const withRetry = this._createAlertRetryWrapper(
      'Unable to load the payout summary',
      signal,
    )
    const summary = await withRetry(() =>
      this._proPayoutStore.fetchPayoutSummary(signal),
    )
    runInAction(() => {
      this.summary = summary
    })
  })
}
